import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

import ImageUploadField from "./ImageUploadField";

const emptyForm = {
  id: "",
  name: "",
  series: "",
  environment: "indoor",
  image_url: "",
  led_type: "",
  pixel_pitch_mm: 2.5,
  cabinet_width_mm: 500,
  cabinet_height_mm: 500,
  cabinet_weight_kg: 8,
  brightness_nits: "",
  refresh_rate_hz: "",
  color_processing_depth: "",
  contrast_ratio: "",
  maintenance: "",
  power_avg_w_per_m2: "",
  power_max_w_per_m2: "",
  price_per_cabinet: "",
  sort_order: 0,
  notes: "",
  active: true,
};

// Spesifikasi tambahan berbentuk daftar label/nilai bebas (bukan JSON
// mentah) supaya bisa diisi langsung oleh tim marketing tanpa perlu tahu
// format teknis. Contoh: "Refresh Rate" -> "3840Hz", "IP Rating" -> "IP65",
// "Garansi" -> "2 Tahun". Muncul di canvas & export PDF LED Configurator.
const emptySpecRow = { label: "", value: "" };

export default function LedProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [specs, setSpecs] = useState([{ ...emptySpecRow }]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    const loadProduct = async () => {
      setLoading(true);
      const { data, error: fetchErr } = await supabase.from("led_products").select("*").eq("id", id).single();
      if (fetchErr) {
        setError(fetchErr.message);
      } else if (data) {
        setForm({ ...emptyForm, ...data });
        const loadedSpecs = Array.isArray(data.extra_specs) && data.extra_specs.length > 0 ? data.extra_specs : [{ ...emptySpecRow }];
        setSpecs(loadedSpecs);
      }
      setLoading(false);
    };
    loadProduct();
  }, [id, isEdit]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateSpec = (idx, key, value) => {
    setSpecs((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  };
  const addSpecRow = () => setSpecs((prev) => [...prev, { ...emptySpecRow }]);
  const removeSpecRow = (idx) => setSpecs((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.id.trim()) {
      setError("ID produk wajib diisi (contoh: ledman-yf-p2-5).");
      return;
    }
    if (!form.name.trim()) {
      setError("Nama produk wajib diisi.");
      return;
    }

    const cleanedSpecs = specs
      .map((s) => ({ label: s.label.trim(), value: s.value.trim() }))
      .filter((s) => s.label || s.value);

    const payload = {
      id: form.id.trim(),
      name: form.name.trim(),
      series: form.series?.trim() || null,
      environment: form.environment,
      image_url: form.image_url || null,
      led_type: form.led_type?.trim() || null,
      pixel_pitch_mm: Number(form.pixel_pitch_mm),
      cabinet_width_mm: Number(form.cabinet_width_mm),
      cabinet_height_mm: Number(form.cabinet_height_mm),
      cabinet_weight_kg: Number(form.cabinet_weight_kg),
      brightness_nits: form.brightness_nits === "" ? null : Number(form.brightness_nits),
      refresh_rate_hz: form.refresh_rate_hz === "" ? null : Number(form.refresh_rate_hz),
      color_processing_depth: form.color_processing_depth?.trim() || null,
      contrast_ratio: form.contrast_ratio?.trim() || null,
      maintenance: form.maintenance?.trim() || null,
      power_avg_w_per_m2: form.power_avg_w_per_m2 === "" ? null : Number(form.power_avg_w_per_m2),
      power_max_w_per_m2: form.power_max_w_per_m2 === "" ? null : Number(form.power_max_w_per_m2),
      price_per_cabinet: form.price_per_cabinet === "" ? null : Number(form.price_per_cabinet),
      sort_order: Number(form.sort_order) || 0,
      notes: form.notes || null,
      active: form.active,
      extra_specs: cleanedSpecs,
    };

    setSaving(true);
    const query = isEdit
      ? supabase.from("led_products").update(payload).eq("id", id)
      : supabase.from("led_products").insert(payload);

    const { error: saveErr } = await query;
    setSaving(false);

    if (saveErr) {
      setError(saveErr.message);
      return;
    }
    navigate("/admin/led-products");
  };

  if (loading) {
    return <div style={{ padding: 28, color: "#94a3b8", fontSize: 13 }}>Memuat produk...</div>;
  }

  return (
    <div style={{ padding: "24px 28px", maxWidth: 720 }}>
      <Link to="/admin/led-products" style={{ fontSize: 12, color: "#64748b", textDecoration: "none" }}>← Kembali ke daftar produk LED</Link>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", marginTop: 10, marginBottom: 20 }}>
        {isEdit ? `Edit: ${form.name}` : "Tambah Produk LED Baru"}
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24 }}>
        <Row label="ID Produk" hint="unik, slug kebab-case, tidak bisa diubah setelah tersimpan">
          <input value={form.id} onChange={(e) => update("id", e.target.value)} disabled={isEdit} placeholder="contoh: ledman-yf-p2-5" style={inputStyle} />
        </Row>

        <Row label="Nama Produk">
          <input value={form.name} onChange={(e) => update("name", e.target.value)} required placeholder="contoh: Ledman YF Series P2.5" style={inputStyle} />
        </Row>

        <Row label="Series / Model" hint="opsional, contoh: P2.5, YF Series -- tampil di label Model pada dokumen Export PDF">
          <input value={form.series || ""} onChange={(e) => update("series", e.target.value)} placeholder="contoh: YF Series" style={inputStyle} />
        </Row>

        <Row label="Foto Produk" hint="tampil di popup 'Pilih Produk' pada LED Configurator">
          <ImageUploadField value={form.image_url} onChange={(url) => update("image_url", url)} productId={form.id} />
        </Row>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Environment">
            <select value={form.environment} onChange={(e) => update("environment", e.target.value)} style={inputStyle}>
              <option value="indoor">Indoor</option>
              <option value="outdoor">Outdoor</option>
            </select>
          </Row>
          <Row label="LED Type" hint="contoh: MIP/COB/SMD">
            <input value={form.led_type || ""} onChange={(e) => update("led_type", e.target.value)} placeholder="MIP/COB/SMD" style={inputStyle} />
          </Row>
        </div>

        <Row label="Pixel Pitch (mm)">
          <input type="number" step="0.01" value={form.pixel_pitch_mm} onChange={(e) => update("pixel_pitch_mm", e.target.value)} required style={inputStyle} />
        </Row>

        <Row label="Dimensi Cabinet dalam mm (Lebar × Tinggi)">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <input type="number" step="1" value={form.cabinet_width_mm} onChange={(e) => update("cabinet_width_mm", e.target.value)} placeholder="Lebar" style={inputStyle} />
            <input type="number" step="1" value={form.cabinet_height_mm} onChange={(e) => update("cabinet_height_mm", e.target.value)} placeholder="Tinggi" style={inputStyle} />
          </div>
        </Row>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Berat per Cabinet (kg)">
            <input type="number" step="0.1" value={form.cabinet_weight_kg} onChange={(e) => update("cabinet_weight_kg", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="Brightness (nits)" hint="opsional">
            <input type="number" step="1" value={form.brightness_nits} onChange={(e) => update("brightness_nits", e.target.value)} style={inputStyle} />
          </Row>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Refresh Rate (Hz)" hint="opsional, contoh: 3840">
            <input type="number" step="1" value={form.refresh_rate_hz ?? ""} onChange={(e) => update("refresh_rate_hz", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="Color Processing Depth" hint="opsional, contoh: 14Bit">
            <input value={form.color_processing_depth || ""} onChange={(e) => update("color_processing_depth", e.target.value)} style={inputStyle} />
          </Row>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Contrast Ratio" hint="opsional, contoh: 15000:1(COB),6500:1(SMD)">
            <input value={form.contrast_ratio || ""} onChange={(e) => update("contrast_ratio", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="Maintenance" hint="opsional, contoh: Front / Front & Back">
            <input value={form.maintenance || ""} onChange={(e) => update("maintenance", e.target.value)} style={inputStyle} />
          </Row>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Daya Rata-rata (W/m²)" hint="opsional, untuk estimasi kW">
            <input type="number" step="1" value={form.power_avg_w_per_m2} onChange={(e) => update("power_avg_w_per_m2", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="Daya Maksimum (W/m²)" hint="opsional">
            <input type="number" step="1" value={form.power_max_w_per_m2} onChange={(e) => update("power_max_w_per_m2", e.target.value)} style={inputStyle} />
          </Row>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Row label="Harga per Cabinet (Rp)" hint="opsional, untuk estimasi internal">
            <input type="number" step="1" value={form.price_per_cabinet} onChange={(e) => update("price_per_cabinet", e.target.value)} style={inputStyle} />
          </Row>
          <Row label="Urutan Tampil" hint="angka lebih kecil tampil lebih dulu">
            <input type="number" step="1" value={form.sort_order} onChange={(e) => update("sort_order", e.target.value)} style={inputStyle} />
          </Row>
        </div>

        <Row label="Catatan Singkat" hint="tampil sebagai deskripsi kecil di bawah nama produk saat dipilih user">
          <textarea value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
        </Row>

        {/* ── Spesifikasi Tambahan: diisi bebas oleh marketing, muncul di
             canvas info & di dokumen Export PDF ── */}
        <div style={{ marginTop: 22, marginBottom: 16, paddingTop: 18, borderTop: "1px solid #f1f5f9" }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>
            Spesifikasi Tambahan
          </label>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 10 }}>
            Isi bebas oleh tim marketing (contoh: Refresh Rate, IP Rating, Garansi, Lifespan, Contrast Ratio). Baris ini akan muncul di dokumen Export PDF LED Configurator.
          </div>

          {specs.map((s, idx) => (
            <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
              <input
                value={s.label}
                onChange={(e) => updateSpec(idx, "label", e.target.value)}
                placeholder="Nama spesifikasi (contoh: Refresh Rate)"
                style={{ ...inputStyle, flex: 1 }}
              />
              <input
                value={s.value}
                onChange={(e) => updateSpec(idx, "value", e.target.value)}
                placeholder="Nilai (contoh: 3840Hz)"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                type="button"
                onClick={() => removeSpecRow(idx)}
                disabled={specs.length === 1}
                title="Hapus baris"
                style={{
                  width: 34, height: 34, flexShrink: 0, borderRadius: 8, border: "1px solid #fecaca",
                  background: "#fef2f2", color: "#dc2626", fontWeight: 700, cursor: specs.length === 1 ? "default" : "pointer",
                  opacity: specs.length === 1 ? 0.4 : 1,
                }}
              >
                ×
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addSpecRow}
            style={{
              padding: "7px 14px", borderRadius: 8, border: "1px dashed #cbd5e1", background: "#f8fafc",
              color: "#3b82f6", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            + Tambah Baris Spesifikasi
          </button>
        </div>

        <Row label="Status">
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155" }}>
            <input type="checkbox" checked={form.active} onChange={(e) => update("active", e.target.checked)} />
            Tampilkan di LED Configurator (aktif)
          </label>
        </Row>

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button
            type="submit"
            disabled={saving}
            style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#3b82f6", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            {saving ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : "Tambah Produk"}
          </button>
          <Link to="/admin/led-products" style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #e2e8f0", color: "#475569", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
            Batal
          </Link>
        </div>
      </form>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1",
  fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};
